import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FormControl, Popper, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Axis, ReferenceLine, XAxis, YAxis } from "interfaces/chart";
import { Autocomplete } from "components/Autocomplete";
import { DatePicker } from "components/DatePicker";

export interface ReferenceSetterProps {
  /** The status of API fetch */
  loading: boolean;
  error: boolean;
  /** Axes to reference */
  xAxes: XAxis[];
  yAxes: YAxis[];
  minDate: Date | null;
  maxDate: Date | null;
  selectedReferenceLine: ReferenceLine | null;
  onChangeReferenceLineAxis: (axis: Axis | null) => void;
  onChangeReferenceLineValue: (value: number | string | Date | null) => void;
}

interface GroupedAxis extends Axis {
  group: "X-Axes" | "Y-Axes";
}

const CustomPopper = (props: any): JSX.Element => {
  return (
    <Popper
      {...props}
      style={{ minWidth: 200, maxWidth: 600 }}
      placement="bottom-end"
    />
  );
};

export const ReferenceSetter = ({
  loading,
  error,
  xAxes,
  yAxes,
  minDate,
  maxDate,
  selectedReferenceLine,
  onChangeReferenceLineAxis,
  onChangeReferenceLineValue
}: ReferenceSetterProps): JSX.Element => {
  const [selectedAxis, setSelectedAxis] = useState<Axis | null>(null);
  const [inputValue, setInputValue] = useState<string | Date | number | null>(
    null
  );
  const [valueTimer, setValueTimer] = useState<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (selectedReferenceLine) {
      const localTargetAxis = [...xAxes, ...yAxes].find(
        axis => axis.id === selectedReferenceLine.targetAxis
      );
      setSelectedAxis(localTargetAxis ? localTargetAxis : null);
      setInputValue(selectedReferenceLine?.value);
    } else {
      setInputValue(null);
    }
  }, [selectedReferenceLine, xAxes, yAxes]);

  useEffect(() => {
    // Clear the previous timer when a new one is set and when component unmounts.
    return () => {
      valueTimer && clearTimeout(valueTimer);
    };
  }, [valueTimer]);

  const groupedAxes = useMemo(() => {
    const newAxes = [];
    if (xAxes && xAxes?.length > 0) {
      newAxes.push(...xAxes.map(xAxis => ({ ...xAxis, group: "X-Axes" })));
    }
    if (yAxes && yAxes?.length > 0) {
      newAxes.push(...yAxes.map(yAxis => ({ ...yAxis, group: "Y-Axes" })));
    }
    return newAxes;
  }, [xAxes, yAxes]);

  const currentPlotType = useMemo(() => {
    const targetAxis = groupedAxes.find(
      axis => axis.id === selectedReferenceLine?.targetAxis
    );
    return targetAxis?.type || "number";
  }, [groupedAxes, selectedReferenceLine?.targetAxis]);

  const handleLocalAxis = useCallback(
    (newAxis: Axis | null) => {
      onChangeReferenceLineAxis(newAxis);
      onChangeReferenceLineValue(null);
      setInputValue(null);
    },
    [onChangeReferenceLineAxis, onChangeReferenceLineValue]
  );

  const handleLocalValue = useCallback(
    (value: string | Date | number | null) => {
      setInputValue(value);
      setValueTimer(() => {
        return setTimeout(() => {
          onChangeReferenceLineValue(value);
        }, 500);
      });
    },
    [onChangeReferenceLineValue]
  );

  return (
    <AxisRangeWrapper>
      <InputWrapper>
        <Autocomplete
          label="Axis"
          placeholder="Select"
          options={groupedAxes}
          loading={loading}
          error={error}
          disabled={
            !minDate || !maxDate || groupedAxes?.length === 0 || loading
          }
          size="small"
          groupBy={(option: { group: "X-Axes" | "Y-Axes" }) => option.group}
          getOptionLabel={(option: GroupedAxis) => option.title?.text}
          isOptionEqualToValue={(option: GroupedAxis, value: Axis) => {
            return option.id === value.id;
          }}
          value={selectedAxis}
          onChange={(_event: React.ChangeEvent<{}>, newAxis: Axis | null) => {
            handleLocalAxis(newAxis);
            setSelectedAxis(newAxis);
          }}
          PopperComponent={CustomPopper}
        />
      </InputWrapper>

      <InputWrapper>
        {currentPlotType === "datetime" ? (
          <DatePicker
            id="reference-date-input"
            slotProps={{
              popper: {
                placement: "left-start"
              }
            }}
            disabled={
              !minDate || !maxDate || !selectedReferenceLine?.targetAxis
            }
            minDate={minDate}
            maxDate={maxDate}
            date={new Date(inputValue ?? "")}
            onChange={(value: Date) =>
              handleLocalValue(value ? new Date(value).getTime() : null)
            }
            fullWidth
            size="small"
            clearable
            label="Date"
            disableFuture
            ariaLabel="change start date"
          />
        ) : (
          <FormControl error variant="outlined" fullWidth>
            <TextField
              name="referenceNumberInput"
              label="Value"
              variant="outlined"
              type="number"
              size="small"
              value={typeof inputValue === "number" ? inputValue : ""}
              onChange={event =>
                handleLocalValue(
                  event.target.value ? Number(event.target.value) : null
                )
              }
              InputProps={{
                inputProps: { step: 0.1 }
              }}
              disabled={
                !minDate || !maxDate || !selectedReferenceLine?.targetAxis
              }
            />
          </FormControl>
        )}
      </InputWrapper>
    </AxisRangeWrapper>
  );
};

const AxisRangeWrapper = styled("div")`
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled("div")`
  padding: ${({ theme }) => `${theme.spacing(1)} 0px`};
`;
